/**
 * useAiExplain hook
 *
 * Manages the explain-with-AI conversation for a selected code snippet.
 * Supports an initial one-shot explanation that auto-fires when triggered,
 * plus unlimited follow-up questions in the same thread.
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import {
  AiExplainState,
  AiChatMessage,
  AiTokenUsage,
  OpenRouterModel,
} from '@/types/ai';
import {
  createAiClient,
  streamChatCompletion,
  ChatMessage,
  calculateCost,
  aggregateTokenUsage,
} from '@/services/ai';
import {
  buildExplainSystemPrompt,
  buildExplainUserMessage,
} from '@/services/aiPrompts';

const INITIAL_STATE: AiExplainState = {
  step: 'idle',
  selectedCode: '',
  contextCode: '',
  messages: [],
  currentContent: '',
};

interface UseAiExplainReturn {
  state: AiExplainState;
  isExplaining: boolean;
  tokenUsage: AiTokenUsage;
  explain: (
    apiKey: string,
    selectedCode: string,
    contextCode: string,
    model: string,
    modelInfo: OpenRouterModel | null,
  ) => Promise<void>;
  sendFollowUp: (
    apiKey: string,
    question: string,
    model: string,
    modelInfo: OpenRouterModel | null,
  ) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

export function useAiExplain(): UseAiExplainReturn {
  const [state, setState] = useState<AiExplainState>(INITIAL_STATE);
  const [modelInfo, setModelInfo] = useState<OpenRouterModel | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Track token usage across all turns
  const [tokenTurns, setTokenTurns] = useState<
    Array<{ prompt: number; completion: number }>
  >([]);

  const isExplaining = state.step === 'explaining';

  const tokenUsage = useMemo((): AiTokenUsage => {
    const aggregated = aggregateTokenUsage(tokenTurns);
    let estimatedCost = 0;
    if (modelInfo) {
      estimatedCost = calculateCost(
        modelInfo,
        aggregated.promptTokens,
        aggregated.completionTokens,
      );
    }
    return { ...aggregated, estimatedCost };
  }, [tokenTurns, modelInfo]);

  /**
   * Run the AI for a given set of messages (shared by explain + follow-up).
   * Appends the assistant's response to the thread when done.
   */
  const runStream = useCallback(
    async (
      apiKey: string,
      model: string,
      messages: ChatMessage[],
    ): Promise<string> => {
      abortControllerRef.current = new AbortController();
      const client = createAiClient(apiKey);

      let fullContent = '';

      await streamChatCompletion(
        client,
        model,
        messages,
        {
          onChunk: (chunk) => {
            fullContent += chunk;
            setState((prev) => ({ ...prev, currentContent: fullContent }));
          },
          onComplete: (_content, usage) => {
            setTokenTurns((prev) => [
              ...prev,
              { prompt: usage.prompt, completion: usage.completion },
            ]);
          },
          onError: (err) => {
            if (err.message !== 'Generation cancelled') {
              setState((prev) => ({
                ...prev,
                step: 'error',
                error: err.message,
                currentContent: '',
              }));
            }
          },
        },
        abortControllerRef.current.signal,
      );

      return fullContent;
    },
    [],
  );

  /**
   * Kick off the initial explanation for a selected code snippet.
   * Stores selectedCode + contextCode in state and auto-sends the first message.
   */
  const explain = useCallback(
    async (
      apiKey: string,
      selectedCode: string,
      contextCode: string,
      model: string,
      newModelInfo: OpenRouterModel | null,
    ) => {
      setModelInfo(newModelInfo);
      setTokenTurns([]);

      const firstUserMessage: AiChatMessage = {
        role: 'user',
        content: buildExplainUserMessage(selectedCode),
      };

      setState({
        step: 'explaining',
        selectedCode,
        contextCode,
        messages: [firstUserMessage],
        currentContent: '',
      });

      const systemPrompt = buildExplainSystemPrompt(contextCode);
      const apiMessages: ChatMessage[] = [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: firstUserMessage.content },
      ];

      try {
        const assistantContent = await runStream(apiKey, model, apiMessages);

        if (assistantContent) {
          setState((prev) => ({
            ...prev,
            step: 'complete',
            messages: [
              ...prev.messages,
              { role: 'assistant', content: assistantContent },
            ],
            currentContent: '',
          }));
        }
      } catch (err) {
        // Cancellations are handled in onError; ignore here
        if (err instanceof Error && err.message === 'Generation cancelled') {
          setState((prev) => ({
            ...prev,
            step: 'idle',
            currentContent: '',
          }));
        }
      }
    },
    [runStream],
  );

  /**
   * Send a follow-up question in the same conversation thread.
   */
  const sendFollowUp = useCallback(
    async (
      apiKey: string,
      question: string,
      model: string,
      newModelInfo: OpenRouterModel | null,
    ) => {
      if (!question.trim()) return;

      setModelInfo(newModelInfo);

      const userMessage: AiChatMessage = { role: 'user', content: question };

      // Optimistically add the user message to the thread
      let updatedMessages: AiChatMessage[] = [];
      setState((prev) => {
        updatedMessages = [...prev.messages, userMessage];
        return {
          ...prev,
          step: 'explaining',
          messages: updatedMessages,
          currentContent: '',
        };
      });

      // Build the full message array for the API: system + all prior turns
      const systemPrompt = buildExplainSystemPrompt(state.contextCode);
      const apiMessages: ChatMessage[] = [
        { role: 'system', content: systemPrompt },
        // Replay the full conversation so the model has context
        ...updatedMessages.map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        })),
      ];

      try {
        const assistantContent = await runStream(apiKey, model, apiMessages);

        if (assistantContent) {
          setState((prev) => ({
            ...prev,
            step: 'complete',
            messages: [
              ...prev.messages,
              { role: 'assistant', content: assistantContent },
            ],
            currentContent: '',
          }));
        }
      } catch (err) {
        if (err instanceof Error && err.message === 'Generation cancelled') {
          setState((prev) => ({
            ...prev,
            step: 'complete', // revert to idle-ish after cancel
            currentContent: '',
          }));
        }
      }
    },
    [runStream, state.contextCode],
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    setState(INITIAL_STATE);
    setTokenTurns([]);
    setModelInfo(null);
  }, []);

  return {
    state,
    isExplaining,
    tokenUsage,
    explain,
    sendFollowUp,
    cancel,
    reset,
  };
}
